"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FixedPriceDiscountPromotion = void 0;
const typings_1 = require("../../typings");
const utils_1 = require("../../utils");
const base_1 = require("../base");
class FixedPriceDiscountPromotion extends base_1.BasePromotion {
    constructor(props) {
        super(props);
        this.props = props;
    }
    /**
     * Get same product sku by selected product sku ids
     * @param productSKUs
     * @returns {ReadonlyArray<ProductSKU>}
     */
    getSameProductSKUBySelectedProductSKUIds(productSKUs) {
        return productSKUs.filter((productSKU) => this.selectedProductIds.includes(productSKU.id));
    }
    /**
     * Apply fixed price for selected product
     * e.g. 3 piece of selected products for 100B
     * @param cart
     * @returns {Cart}
     */
    applyFixedPriceForSelectedProductPromotion(cart) {
        const isBundled = this.bundleSetting;
        if (isBundled) {
            throw new Error('Could not apply fixed price promotion for X piece of selected product that is mark as bundled');
        }
        if (!cart.productSKUs.length) {
            return cart;
        }
        const sameProductSKUsWithSelectedProducts = this.getSameProductSKUBySelectedProductSKUIds(cart.productSKUs);
        if (sameProductSKUsWithSelectedProducts.length) {
            const hasAppliedFixedPricePromotionOnSameProductsBefore = sameProductSKUsWithSelectedProducts.some((productSKU) => {
                var _a;
                if (!((_a = productSKU.promotions) === null || _a === void 0 ? void 0 : _a.length)) {
                    return false;
                }
                return productSKU.promotions.some((promotion) => promotion.settings.type === typings_1.PromotionSettingsType.FIXED_PRICE_DISCOUNT);
            });
            if (hasAppliedFixedPricePromotionOnSameProductsBefore) {
                return cart;
            }
        }
        // has been applied flag for checking whether the system should apply promotion again or not
        let hasBeenAppliedAndNonRepeatable = false;
        // total discount price for particular promotion
        let totalProductDiscount = 0;
        const newCart = Object.assign({}, cart);
        const productSKUsWithAppliedPromotion = newCart.productSKUs.map((productSKU) => {
            /**
             * If productSKU is free, don't take it into account
             * Skip it completely
             */
            if (productSKU.isFree) {
                return productSKU;
            }
            /**
             * Check whether previous product sku has been applied with
             * particular promotion or not, if yes, then proceed to skip
             * it after once
             */
            if (hasBeenAppliedAndNonRepeatable) {
                return productSKU;
            }
            /**
             * Sku does not belong to promotion
             * calculate total price of current item
             * then proceed to next iteration
             */
            if (!this.selectedProductIds.includes(productSKU.id)) {
                return productSKU;
            }
            /**
             * case: sku amount is less than threshold amount
             * e.g.
             * threshold amount: 3
             * sku amount: 2, price: 30
             */
            const matchedCriteria = this.getCriteriaByTarget(productSKU.amount);
            if (!matchedCriteria) {
                return productSKU;
            }
            const discount = (0, utils_1.getPromotionDiscountCriteria)(matchedCriteria);
            if (discount === undefined) {
                return productSKU;
            }
            /**
             * Sku amount matches condition but it appears that total price of sku
             * is less than fixed discount. It doesn't make sense to apply the promotion
             * to one that has total price less than fixed discount.
             * e.g.
             * sku amount: 3
             * sku price: 30
             * threshold: 3
             *
             * total price: 90
             * fixed amount: 100
             */
            const totalPriceOfCurrentProductSKU = productSKU.amount * productSKU.price;
            if (discount >= totalPriceOfCurrentProductSKU) {
                return productSKU;
            }
            /**
             * case: sku amount equals to threshold amount
             * e.g.
             * threshold amount: 3
             * sku amount: 3, price: 30
             */
            if ((0, utils_1.isMetThresholdCriteriaExactly)(matchedCriteria, productSKU.amount)) {
                const totalDiscount = totalPriceOfCurrentProductSKU - discount;
                totalProductDiscount += totalDiscount;
                return Object.assign(Object.assign({}, productSKU), { promotions: this.addAppliedPromotion(productSKU.promotions, { totalDiscount }) });
            }
            /**
             * case: sku amount is more than threshold amount
             * e.g.
             * threshold amount: 3
             * sku amount: 5, price: 30
             */
            // const multiplier = this.getMultiplier(productSKU.amount, this.amountThreshold)
            const multiplier = (0, utils_1.getMultiplierByPromotionCriteriaAndDividend)(matchedCriteria, productSKU.amount, this.isAutoGeneratedCriteria, this.isRepeatable);
            const amountThreshold = (0, utils_1.getThresholdAmountFromCriteria)(matchedCriteria);
            const remainingAmount = productSKU.amount - amountThreshold * multiplier;
            const remainingTotalPrice = remainingAmount * productSKU.price;
            const totalPriceAfterDiscount = discount * multiplier + remainingTotalPrice;
            const totalDiscount = totalPriceOfCurrentProductSKU - totalPriceAfterDiscount;
            totalProductDiscount += totalDiscount;
            // Mark applied promotion for non-repeatable
            if (!this.isRepeatable) {
                hasBeenAppliedAndNonRepeatable = true;
            }
            return Object.assign(Object.assign({}, productSKU), { promotions: this.addAppliedPromotion(productSKU.promotions, { totalDiscount }) });
        });
        return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount: totalProductDiscount });
    }
    /**
     * Apply fixed price for selected product in bundle
     * e.g. 3 piece of mix selected product for 100B
     * @param cart
     * @returns {Cart}
     */
    applyFixedPriceForSelectedProductInBundlePromotion(cart) {
        const isBundled = this.bundleSetting;
        if (!isBundled) {
            throw new Error('Could not apply fixed price promotion for X piece of selected product in bundle that is not mark as bundled');
        }
        if (!cart.productSKUs.length) {
            return cart;
        }
        const sameProductSKUsWithSelectedProducts = this.getSameProductSKUBySelectedProductSKUIds(cart.productSKUs);
        if (sameProductSKUsWithSelectedProducts.length) {
            const hasAppliedFixedPricePromotionOnSameProductsBefore = sameProductSKUsWithSelectedProducts.some((productSKU) => {
                var _a;
                if (!((_a = productSKU.promotions) === null || _a === void 0 ? void 0 : _a.length)) {
                    return false;
                }
                return productSKU.promotions.some((promotion) => promotion.settings.type === typings_1.PromotionSettingsType.FIXED_PRICE_DISCOUNT);
            });
            if (hasAppliedFixedPricePromotionOnSameProductsBefore) {
                return cart;
            }
        }
        // matched product skus that are less than threshold and total discount price for particular promotion
        let matchedProductSKUsThatAreLessThanThreshold = [];
        let totalProductDiscount = 0;
        const newCart = Object.assign({}, cart);
        const sortedProductSKUs = (0, utils_1.sortCartByAwardFromLowToHigh)(newCart.productSKUs, this.isAwardFromLowToHigh);
        // Total amount of products in shopping cart that are selected products
        const totalAmountOfSelectedProductsInShoppingCart = (0, utils_1.getTotalAmountOfSelectedProducts)(sortedProductSKUs, this.selectedProductIds);
        // has been applied flag for checking whether the system should apply promotion again or not
        let hasBeenAppliedAndNonRepeatable = false;
        const matchedCriteria = this.getCriteriaByTarget(totalAmountOfSelectedProductsInShoppingCart);
        if (!matchedCriteria) {
            return cart;
        }
        const discount = (0, utils_1.getPromotionDiscountCriteria)(matchedCriteria);
        if (discount === undefined) {
            return cart;
        }
        const amountThreshold = (0, utils_1.getThresholdAmountFromCriteria)(matchedCriteria);
        /**
         * Total amount of selected products in shopping cart is more than
         * or equals to threshold amount (Match promotion threshold)
         */
        const sortedProductSKUsWithAppliedPromotion = sortedProductSKUs.map((productSKU) => {
            /**
             * If productSKU is free, don't take it into account
             * Skip it completely
             */
            if (productSKU.isFree) {
                return productSKU;
            }
            /**
             * Check whether previous product sku has been applied with
             * particular promotion or not, if yes, then proceed to skip
             * it after once
             */
            if (hasBeenAppliedAndNonRepeatable) {
                return productSKU;
            }
            const totalPriceOfCurrentProductSKU = productSKU.amount * productSKU.price;
            /**
             * Sku does not belong to promotion
             * calculate total price of current item
             * then proceed to next iteration
             */
            if (!this.selectedProductIds.includes(productSKU.id)) {
                return productSKU;
            }
            /**
             * Matched items are less than threshold from last
             * iteration
             */
            if (matchedProductSKUsThatAreLessThanThreshold.length) {
                // Total amount from matched items
                const totalAmountOfMatchedProductSKUs = (0, utils_1.getTotalAmount)(matchedProductSKUsThatAreLessThanThreshold);
                // Total amount + current amount
                const totalAmount = totalAmountOfMatchedProductSKUs + productSKU.amount;
                /**
                 * Combined amount of matched item and current item are still
                 * less than threshold amount, keep it for next iteration
                 */
                if (!(0, utils_1.isMetThresholdCriteria)(matchedCriteria, totalAmount)) {
                    matchedProductSKUsThatAreLessThanThreshold = matchedProductSKUsThatAreLessThanThreshold.concat([productSKU]);
                    return Object.assign(Object.assign({}, productSKU), { promotions: this.addAppliedPromotion(productSKU.promotions) });
                }
                // Calculate actual total price from matched items from each item amount and price
                const totalPriceOfMatchedProductSKUs = (0, utils_1.getTotalPrice)(matchedProductSKUsThatAreLessThanThreshold);
                /**
                 * case: sku amount equals to threshold amount
                 * e.g.
                 * threshold amount: 3
                 * 1st amount: 1, price: 30
                 * 2nd amount: 2, price: 40
                 */
                if ((0, utils_1.isMetThresholdCriteriaExactly)(matchedCriteria, totalAmount)) {
                    // Combined total price of matched item and current item (Actual price of the whole thing without discount)
                    const actualPrice = totalPriceOfMatchedProductSKUs + totalPriceOfCurrentProductSKU;
                    const totalDiscount = actualPrice - discount;
                    totalProductDiscount += totalDiscount;
                    // Mark applied promotion for non-repeatable
                    if (!this.isRepeatable) {
                        hasBeenAppliedAndNonRepeatable = true;
                    }
                    // Reset matchedItemsThatAreLessThanThreshold array after the calculation
                    matchedProductSKUsThatAreLessThanThreshold = [];
                    return Object.assign(Object.assign({}, productSKU), { promotions: this.addAppliedPromotion(productSKU.promotions, { totalDiscount }) });
                }
                /**
                 * case: sku amount is more than threshold amount
                 * e.g.
                 * threshold amount: 3
                 * 1st amount: 2, price: 30
                 * 2nd amount: 2, price: 40
                 */
                const multiplier = (0, utils_1.getMultiplierByPromotionCriteriaAndDividend)(matchedCriteria, totalAmount, this.isAutoGeneratedCriteria, this.isRepeatable);
                const remainingAmount = totalAmount - amountThreshold * multiplier;
                const totalPriceAfterDiscount = discount * multiplier;
                const totalPriceOfCurrentProductSKUWithMatchedAmount = productSKU.price * (productSKU.amount - remainingAmount);
                const actualPrice = totalPriceOfMatchedProductSKUs + totalPriceOfCurrentProductSKUWithMatchedAmount;
                const totalDiscount = actualPrice - totalPriceAfterDiscount;
                totalProductDiscount += totalDiscount;
                // Mark applied promotion for non-repeatable
                if (!this.isRepeatable) {
                    hasBeenAppliedAndNonRepeatable = true;
                }
                // Reset matchedItemsThatAreLessThanThreshold array after the calculation
                matchedProductSKUsThatAreLessThanThreshold = [Object.assign(Object.assign({}, productSKU), { amount: remainingAmount })];
                return Object.assign(Object.assign({}, productSKU), { promotions: this.addAppliedPromotion(productSKU.promotions, { totalDiscount }) });
            }
            if ((0, utils_1.isMetThresholdCriteria)(matchedCriteria, productSKU.amount)) {
                /**
                 * Sku amount matches condition but it appears that total price of sku
                 * is less than fixed discount. It doesn't make sense to apply the promotion
                 * to one that has total price less than fixed discount.
                 * e.g.
                 * sku amount: 3
                 * sku price: 30
                 * threshold: 3
                 *
                 * total price: 90
                 * fixed amount: 100
                 *
                 * or
                 *
                 * sku amount: 5
                 * sku price: 20
                 * threshold: 3
                 *
                 * total price: 100
                 * fixed amount: 100
                 */
                if (discount >= totalPriceOfCurrentProductSKU) {
                    return productSKU;
                }
                /**
                 * case: sku amount equals to threshold amount
                 * e.g.
                 * threshold amount: 3
                 * 1st amount: 1, price: 30
                 * 2nd amount: 2, price: 40
                 */
                if ((0, utils_1.isMetThresholdCriteriaExactly)(matchedCriteria, productSKU.amount)) {
                    const totalDiscount = totalPriceOfCurrentProductSKU - discount;
                    totalProductDiscount += totalDiscount;
                    // Mark applied promotion for non-repeatable
                    if (!this.isRepeatable) {
                        hasBeenAppliedAndNonRepeatable = true;
                    }
                    return Object.assign(Object.assign({}, productSKU), { promotions: this.addAppliedPromotion(productSKU.promotions, { totalDiscount }) });
                }
                /**
                 * case: sku amount is more than threshold amount and remaining amount is more than 0
                 * meaning that purchase amount is not enough for multiplication of threshold amount
                 * e.g.
                 * threshold amount: 3
                 * purchase amount: 4
                 * multiplier = 4 / 3 => 1.33 ~ 1
                 * remainingAmount = 4 - (3 * 1) => 1
                 *
                 * apply promotion with only threshold amount then
                 * push remaining amount to matchedItemsThatAreLessThanThreshold array
                 *
                 * e.g.
                 * threshold amount: 3
                 * 1st amount: 2, price: 30
                 * 2nd amount: 2, price: 40
                 */
                const multiplier = (0, utils_1.getMultiplierByPromotionCriteriaAndDividend)(matchedCriteria, productSKU.amount, this.isAutoGeneratedCriteria, this.isRepeatable);
                const remainingAmount = productSKU.amount - amountThreshold * multiplier;
                if (remainingAmount > 0) {
                    const totalPriceOfMatchedAmount = amountThreshold * productSKU.price;
                    const totalDiscount = totalPriceOfMatchedAmount - discount;
                    totalProductDiscount += totalDiscount * multiplier;
                    // Mark applied promotion for non-repeatable
                    if (!this.isRepeatable) {
                        hasBeenAppliedAndNonRepeatable = true;
                    }
                    // Push remaining amount to an array
                    matchedProductSKUsThatAreLessThanThreshold = matchedProductSKUsThatAreLessThanThreshold.concat([
                        Object.assign(Object.assign({}, productSKU), { amount: remainingAmount }),
                    ]);
                    return Object.assign(Object.assign({}, productSKU), { promotions: this.addAppliedPromotion(productSKU.promotions, { totalDiscount }) });
                }
                /**
                 * case sku amount is match with threshold amount upon multiplication
                 * e.g.
                 * threshold amount: 3
                 * purchase amount: 6
                 * multiplier = 6 / 3 => 2
                 * remainingAmount = 6 - 3 * 2 => 0
                 */
                const totalDiscount = totalPriceOfCurrentProductSKU - discount * multiplier;
                totalProductDiscount += totalDiscount;
                // Mark applied promotion for non-repeatable
                if (!this.isRepeatable) {
                    hasBeenAppliedAndNonRepeatable = true;
                }
                return Object.assign(Object.assign({}, productSKU), { promotions: this.addAppliedPromotion(productSKU.promotions, { totalDiscount }) });
            }
            // Add current item to matched items that do not match the threshold
            matchedProductSKUsThatAreLessThanThreshold = matchedProductSKUsThatAreLessThanThreshold.concat([productSKU]);
            return Object.assign(Object.assign({}, productSKU), { promotions: this.addAppliedPromotion(productSKU.promotions) });
        });
        const productSKUsWithAppliedPromotion = (0, utils_1.mergeProductSKUsInformation)(newCart.productSKUs, sortedProductSKUsWithAppliedPromotion);
        return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount: totalProductDiscount });
    }
    /**
     * Apply promotion
     * @param cart
     * @returns {Cart}
     */
    applyPromotion(cart) {
        switch (this.settingCondition) {
            case typings_1.PromotionCondition.BUY_X_PIECE_OF_SELECTED_PRODUCT:
                return this.applyFixedPriceForSelectedProductPromotion(cart);
            case typings_1.PromotionCondition.BUY_X_PIECE_OF_SELECTED_PRODUCT_IN_BUNDLE:
                return this.applyFixedPriceForSelectedProductInBundlePromotion(cart);
            default:
                return cart;
        }
    }
}
exports.FixedPriceDiscountPromotion = FixedPriceDiscountPromotion;
//# sourceMappingURL=fixed-price.js.map